// Globals
import { FC } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import { Modal, ModalSize } from '@/components';
import ImportFaqForm from '../ImportFaqForm/ImportFaqForm';
import ImportFaqStats from '../ImportFaqStats/ImportFaqStats';

// Helpers
import { TFaqImportFormValues } from '../../helpers';

// Store
import { useAppDispatch, useAppState } from '@/store';
import {
  applyFaqImport,
  previewFaqImport,
  resetFaqImport,
  selectFaqImportFile,
  selectFaqImportPreview,
  selectIsApplyingFaqImport,
  selectIsPreviewingFaqImport,
  setFaqImportFile,
} from '../../feature';

type TImportFaqModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onImported: () => void;
  isGeneral?: boolean;
  channelId?: number;
};

const ImportFaqModal: FC<TImportFaqModalProps> = ({ isOpen, onClose, onImported, isGeneral = false, channelId }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const selectedFile = useAppState(selectFaqImportFile);
  const preview = useAppState(selectFaqImportPreview);
  const isPreviewing = useAppState(selectIsPreviewingFaqImport);
  const isApplying = useAppState(selectIsApplyingFaqImport);

  const handleClose = () => {
    dispatch(resetFaqImport());
    onClose();
  };

  const handlePreviewImport = async ({ file }: TFaqImportFormValues) => {
    dispatch(setFaqImportFile(file));
    try {
      await dispatch(previewFaqImport({ file, isGeneral, channelId })).unwrap();
    } catch {}
  };

  const handleChooseDifferentFile = () => {
    dispatch(resetFaqImport());
  };

  const handleApplyImport = async () => {
    if (!preview || !selectedFile) return;
    try {
      await dispatch(
        applyFaqImport({ file: selectedFile, importDigest: preview.importDigest, isGeneral, channelId }),
      ).unwrap();
      onImported();
      handleClose();
    } catch {}
  };

  return (
    <Modal
      destroyOnClose
      title={t('title_import_faqs')}
      onCancel={handleClose}
      visible={isOpen}
      size={ModalSize.MEDIUM}
    >
      {!preview ? (
        <ImportFaqForm onSubmit={handlePreviewImport} isLoading={isPreviewing} />
      ) : (
        <ImportFaqStats
          preview={preview}
          isApplying={isApplying}
          onApply={handleApplyImport}
          onChooseDifferentFile={handleChooseDifferentFile}
        />
      )}
    </Modal>
  );
};

export default ImportFaqModal;
